import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Ban, Plus, Loader2 } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Field, inputCls, btnPrimary } from "@/components/Form";

const STATUS = { available: ["Disponível", "bg-emerald-50 text-emerald-700"], redeemed: ["Resgatada", "bg-sky-50 text-sky-700"], active: ["Ativa", "bg-sky-50 text-sky-700"], revoked: ["Revogada", "bg-slate-100 text-slate-500"] };
const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copiado"));

function CodeForm({ onDone }) {
  const [f, setF] = useState({ quantity: 1, days: 30, note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/licenses/codes", { ...f, quantity: Number(f.quantity), days: Number(f.days) });
      toast.success(`${data.length} código(s) gerado(s)`); onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-[120px_120px_1fr_auto] sm:items-end" data-testid="license-code-form">
      <Field label="Quantidade"><input data-testid="codes-quantity-input" type="number" min={1} max={100} required className={inputCls} value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} /></Field>
      <Field label="Dias"><input data-testid="codes-days-input" type="number" min={1} max={3650} required className={inputCls} value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} /></Field>
      <Field label="Observação"><input data-testid="codes-note-input" className={inputCls} placeholder="Ex.: parceiros" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
      <button data-testid="codes-generate-btn" disabled={busy} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Gerar códigos</button>
    </form>
  );
}

function LicenseRow({ l, onRevoke }) {
  const [label, cls] = STATUS[l.status] || [l.status, ""];
  return (
    <tr className="border-t border-slate-100" data-testid={`license-row-${l.license_id}`}>
      <td className="px-4 py-3">
        {l.code ? <button onClick={() => copy(l.code)} data-testid={`license-copy-${l.license_id}`} className="inline-flex items-center gap-1.5 font-mono text-sm font-bold text-slate-900 hover:text-amber-700">{l.code}<Copy className="h-3.5 w-3.5" /></button>
          : <span className="text-sm text-slate-500">Liberação direta</span>}
      </td>
      <td className="px-4 py-3 text-sm">{l.days} dias</td>
      <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`} data-testid={`license-status-${l.license_id}`}>{label}</span></td>
      <td className="px-4 py-3 text-sm">{l.user_email || "—"}</td>
      <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(l.created_at)}{l.expires_at ? ` · até ${fmtDate(l.expires_at)}` : ""}{l.note ? ` · ${l.note}` : ""}</td>
      <td className="px-4 py-3">{l.status !== "revoked" && <button data-testid={`license-revoke-${l.license_id}`} onClick={() => onRevoke(l)} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-red-200 px-3 text-xs font-semibold text-red-600 hover:bg-red-50"><Ban className="h-3.5 w-3.5" />Revogar</button>}</td>
    </tr>
  );
}

export default function LicensesPanel() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/licenses").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async (l) => {
    const msg = l.user_email ? `Revogar e remover ${l.days} dias da validade de ${l.user_email}?` : "Revogar este código? Ele não poderá mais ser resgatado.";
    if (!window.confirm(msg)) return;
    try { await api.post(`/admin/licenses/${l.license_id}/revoke`); toast.success("Licença revogada"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <CodeForm onDone={load} />
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="licenses-table">
        <table className="w-full min-w-[820px] text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{["Código", "Período", "Status", "Cliente", "Detalhes", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{items.map((l) => <LicenseRow key={l.license_id} l={l} onRevoke={revoke} />)}</tbody>
        </table>
        {!items.length && <p className="p-6 text-center text-sm text-slate-500">Nenhuma licença ainda. Gere códigos acima ou libere direto na aba Clientes.</p>}
      </div>
    </div>
  );
}
